import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Brain, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface PsychometrySession {
  id: string;
  session_date: string;
  created_at: string;
  object_description: string;
  initial_impressions: string;
  sensory_details: string;
  emotional_impressions: string;
  visual_impressions: string;
  other_impressions: string;
  validation_received: boolean;
  accuracy_rating: number;
  validation_notes: string;
}

export default function PsychometryLog() {
  const [sessions, setSessions] = useState<PsychometrySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    object_description: '',
    initial_impressions: '',
    sensory_details: '',
    emotional_impressions: '',
    visual_impressions: '',
    other_impressions: '',
    validation_received: false,
    accuracy_rating: 5,
    validation_notes: ''
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('psychometry_sessions')
      .select('*')
      .eq('user_id', user.id)
      .order('session_date', { ascending: false });

    if (error) {
      toast({ title: 'Error loading sessions', variant: 'destructive' });
    } else {
      setSessions(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('psychometry_sessions')
      .insert({
        user_id: user.id,
        session_date: new Date().toISOString().split('T')[0],
        ...formData
      });

    if (error) {
      toast({ title: 'Error saving session', variant: 'destructive' });
    } else {
      toast({ title: 'Psychometry session saved' });
      setFormData({
        object_description: '',
        initial_impressions: '',
        sensory_details: '',
        emotional_impressions: '',
        visual_impressions: '',
        other_impressions: '',
        validation_received: false,
        accuracy_rating: 5,
        validation_notes: ''
      });
      setShowForm(false);
      loadSessions();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading psychometry log...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary animate-pulse" />
            Psychometry Practice Log
          </CardTitle>
          <CardDescription>
            Log impressions from objects, photos, and personal belongings. Psychometry is reading energy imprints from physical items. Track what you sense and validate your accuracy over time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowForm(!showForm)} className="mb-4">
            <Plus className="h-4 w-4 mr-2" />
            Log New Session
          </Button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 border border-border rounded-lg bg-card animate-fade-in">
              <div className="space-y-2">
                <Label>Object Description</Label>
                <Input
                  value={formData.object_description}
                  onChange={(e) => setFormData({ ...formData, object_description: e.target.value })}
                  placeholder="Ring, photo, letter, jewelry, etc."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Initial Impressions</Label>
                <Textarea
                  value={formData.initial_impressions}
                  onChange={(e) => setFormData({ ...formData, initial_impressions: e.target.value })}
                  placeholder="First feelings or thoughts when holding the object..."
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Sensory Details (Touch, Temperature, Texture)</Label>
                <Textarea
                  value={formData.sensory_details}
                  onChange={(e) => setFormData({ ...formData, sensory_details: e.target.value })}
                  placeholder="Hot/cold spots, tingling, heaviness, lightness..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Emotional Impressions</Label>
                <Textarea
                  value={formData.emotional_impressions}
                  onChange={(e) => setFormData({ ...formData, emotional_impressions: e.target.value })}
                  placeholder="Joy, sadness, anxiety, love, anger felt from the object..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Visual Impressions</Label>
                <Textarea
                  value={formData.visual_impressions}
                  onChange={(e) => setFormData({ ...formData, visual_impressions: e.target.value })}
                  placeholder="Any images, scenes, faces, or symbols seen..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Other Impressions</Label>
                <Textarea
                  value={formData.other_impressions}
                  onChange={(e) => setFormData({ ...formData, other_impressions: e.target.value })}
                  placeholder="Names, dates, locations, or other information received..."
                  rows={2}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="validation"
                  checked={formData.validation_received}
                  onCheckedChange={(checked) => setFormData({ ...formData, validation_received: checked as boolean })}
                />
                <label htmlFor="validation" className="text-sm font-light">
                  Validation received
                </label>
              </div>

              {formData.validation_received && (
                <div className="space-y-2 animate-fade-in">
                  <Label>Validation Notes</Label>
                  <Textarea
                    value={formData.validation_notes}
                    onChange={(e) => setFormData({ ...formData, validation_notes: e.target.value })}
                    placeholder="What was confirmed? What was off?"
                    rows={2}
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button type="submit">Save Session</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {sessions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Start practicing psychometry to develop your ability to read energy from objects.
              </p>
            ) : (
              sessions.map((session) => (
                <Card key={session.id} className="bg-muted/30 animate-fade-in">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-lg">{session.object_description}</CardTitle>
                        {session.validation_received && (
                          <Badge variant="outline" className="flex items-center gap-1 w-fit">
                            <CheckCircle className="h-3 w-3" />
                            Validated
                          </Badge>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(session.session_date), 'PPP')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-light text-primary mb-1">Initial Impressions:</p>
                      <p className="text-sm text-muted-foreground font-light">{session.initial_impressions}</p>
                    </div>
                    {session.emotional_impressions && (
                      <div>
                        <p className="text-sm font-light text-primary mb-1">Emotional:</p>
                        <p className="text-sm text-muted-foreground font-light">{session.emotional_impressions}</p>
                      </div>
                    )}
                    {session.visual_impressions && (
                      <div>
                        <p className="text-sm font-light text-primary mb-1">Visual:</p>
                        <p className="text-sm text-muted-foreground font-light">{session.visual_impressions}</p>
                      </div>
                    )}
                    {session.validation_notes && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-sm font-light text-primary mb-1">Validation:</p>
                        <p className="text-sm text-muted-foreground font-light">{session.validation_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
